import { Button, Checkbox, Modal } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useRateUpdate } from '../../hooks/apiHooks/rate';
import { validateRateClasses } from '../../library/util';
import { Class } from '../../models/Class';
import { Rate } from '../../models/Rate';

interface IProps {
  rate: Rate;
  classes: Class[];
  onSubmit: () => void;
  onClose: () => void;
}

const ValidateRateClassesModal: React.FC<IProps> = ({
  classes,
  onClose,
  onSubmit,
  rate,
}) => {
  const updateMutation = useRateUpdate();
  const [selectedClassIds, setSelectedClassIds] = useState<number[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  // initialize with all class IDs that have rates
  useEffect(() => {
    const classIdsWithRates = classes
      .filter((cls) => rate.classRate[cls.id]?.value !== undefined)
      .map((cls) => cls.id);
    setSelectedClassIds(classIdsWithRates);
  }, [classes, rate.classRate]);

  const handleToggleAll = useCallback(() => {
    const classIdsWithRates = classes
      .filter((cls) => rate.classRate[cls.id]?.value !== undefined)
      .map((cls) => cls.id);

    if (selectedClassIds.length === classIdsWithRates.length) {
      setSelectedClassIds([]);
    } else {
      setSelectedClassIds(classIdsWithRates);
    }
  }, [classes, rate.classRate, selectedClassIds.length]);

  const handleToggleClass = useCallback(
    (classId: number) => {
      if (selectedClassIds.includes(classId)) {
        setSelectedClassIds(selectedClassIds.filter((id) => id !== classId));
      } else {
        setSelectedClassIds([...selectedClassIds, classId]);
      }
    },
    [selectedClassIds]
  );

  const validateRate = useCallback(() => {
    if (selectedClassIds.length === 0) {
      return;
    }

    setIsValidating(true);

    validateRateClasses(rate, selectedClassIds, updateMutation)
      .then(() => {
        setIsValidating(false);
        onSubmit();
      })
      .catch((error) => {
        console.error('Error validating rate:', error);
        setIsValidating(false);
      });
  }, [rate, selectedClassIds, updateMutation, onSubmit]);

  const classIdsWithRates = useMemo(
    () =>
      classes
        .filter((cls) => rate.classRate[cls.id]?.value !== undefined)
        .map((cls) => cls.id),
    [classes, rate.classRate]
  );

  const allSelected = useMemo(
    () =>
      classIdsWithRates.length > 0 &&
      selectedClassIds.length === classIdsWithRates.length,
    [classIdsWithRates.length, selectedClassIds.length]
  );

  return (
    <Modal
      centered
      open
      footer={null}
      maskClosable={false}
      title="Validate Rate Classes"
      width={400}
      onCancel={onClose}
    >
      <div style={{ marginBottom: 16 }}>
        <Checkbox checked={allSelected} onChange={handleToggleAll}>
          Select All
        </Checkbox>
      </div>
      <div style={{ marginBottom: 16, maxHeight: 300, overflowY: 'auto' }}>
        {classes
          .filter((cls) => rate.classRate[cls.id]?.value !== undefined)
          .map((cls) => (
            <div key={cls.id} style={{ marginBottom: 8 }}>
              <Checkbox
                checked={selectedClassIds.includes(cls.id)}
                onChange={() => handleToggleClass(cls.id)}
              >
                {cls.name} (${rate.classRate[cls.id]?.value?.toFixed(2)})
              </Checkbox>
            </div>
          ))}
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          disabled={selectedClassIds.length === 0}
          loading={isValidating}
          type="primary"
          onClick={validateRate}
        >
          Validate
        </Button>
      </div>
    </Modal>
  );
};

export default ValidateRateClassesModal;
